import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ComponentPropsWithoutRef } from "react";

import { cn } from "core/shadcn/lib/utils";

const stackVariants = cva("flex", {
  variants: {
    direction: {
      row: "flex-row",
      column: "flex-col",
    },
    gap: {
      none: "gap-0",
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
    },
    align: {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      baseline: "items-baseline",
      stretch: "items-stretch",
    },
    justify: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
    },
    wrap: {
      true: "flex-wrap",
    },
  },
  defaultVariants: {
    direction: "row",
    gap: "sm",
  },
});

/**
 * A flex row or column with one of the shared gaps (4, 8, 16 or 24 px).
 * Renders a `div`; pass `asChild` with a single child element (`ul`,
 * `section`) to lay out that element instead. Overrides go in `className`
 * here, not on the child.
 */
export const Stack = forwardRef<
  HTMLElement,
  ComponentPropsWithoutRef<"div"> &
    VariantProps<typeof stackVariants> & { asChild?: boolean }
>(function Stack(
  {
    asChild = false,
    className,
    direction,
    gap,
    align,
    justify,
    wrap,
    children,
    ...props
  },
  ref,
) {
  // Always rendering through Slot keeps the ref typed as HTMLElement, which
  // also fits whatever element asChild passes in.
  return (
    <Slot
      className={cn(
        stackVariants({ direction, gap, align, justify, wrap }),
        className,
      )}
      ref={ref}
      {...props}
    >
      {asChild ? children : <div>{children}</div>}
    </Slot>
  );
});
